import { useEffect, useState } from 'react';
import type { ComboboxItem } from '@screentone/core';
import { Box, FormHelperText, Group, MultiSelect, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import {
  ItpIssuePageType,
  PublicationSettingUpdateInput,
  usePublicationSettingUpdateMutation
} from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';

const itpPageTypes = Object.values(ItpIssuePageType);
const toMultiselectItem = (value: string): ComboboxItem => ({ id: value, label: value, value });

const PublicationSettingsItpRequiredPageTypes = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  const { mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const selectedItpPageTypes = currentPublicationSetting?.itpRequiredPageTypes ?? [];

  const handleItpRequiredPageTypesChange = (
    items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
  ) => {
    if (currentPublicationSetting === null) {
      return;
    }
    const resolvedItems = typeof items === 'function' ? items(selectedItpPageTypes.map(toMultiselectItem)) : items;
    setCurrentPublicationSetting({
      ...currentPublicationSetting,
      itpRequiredPageTypes: resolvedItems.map((item) => item.value as ItpIssuePageType)
    });
    setHasChanged(true);
  };

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    const newPublicationSetting: PublicationSettingUpdateInput = { ...currentPublicationSetting };

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: async () => {
          setCurrentPublicationSetting(newPublicationSetting);
          setHasChanged(false);
          alertSuccess('Publication settings saved successfully');

          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  if (currentProperty !== 'wsj') {
    return (
      <Group gap="lg" direction="column">
        <Group.Item fullWidth>
          <Typography variant="note">
            This setting is only available on WSJ. Switch to the WSJ property to edit it.
          </Typography>
        </Group.Item>
      </Group>
    );
  }

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>ITP required page types</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  Page types that require an ITP (In Today&apos;s Paper) placement. Editors are blocked from publishing
                  an article to one of these page types without an ITP entry.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <MultiSelect
                      label="ITP Required Page Types"
                      disabled={isLoading || isReadOnly}
                      items={itpPageTypes.map(toMultiselectItem)}
                      placeholder="Select ITP required page types"
                      selectedItems={selectedItpPageTypes.map(toMultiselectItem)}
                      onSelectedItemsChange={handleItpRequiredPageTypesChange}
                      resetPosition="inside"
                    />
                    <FormHelperText>
                      Add every page type whose articles must have a matching ITP entry before publishing.
                    </FormHelperText>
                  </Group>
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsItpRequiredPageTypes;
